/*
 * Purpose: CareerOS private connector — userspace only (not in builtins)
 * Last modified: 2026-07-11 | Agent
 * Completeness: 95
 */
import type { AutoBlocksConnector } from '@/services/connectors/library/types';

export const CAREEROS_CONNECTOR_ID = 'ab.custom.careeros';
export const CAREEROS_MCP_SERVER_ID = 'careeros';
export const CAREEROS_WORKFLOW_SEED_KEY = 'careeros_tailored_resume_jd';

const ts = () => new Date().toISOString();

/** Tenant-local connector — registered via userspace bootstrap, never in BUILTIN_CONNECTORS. */
export function buildCareerOSPrivateConnector(): AutoBlocksConnector {
  return {
    id: CAREEROS_CONNECTOR_ID,
    name: 'CareerOS',
    vendor: 'ai_generated',
    category: 'career',
    description:
      'Private CareerOS resume studio — tailored resumes, canonical CV, ATS validation, and semantic search over your career knowledge base.',
    auth: 'secret',
    patternRef: 'careeros-mcp-userspace',
    runtime: 'edge',
    status: 'beta',
    version: 1,
    createdAt: ts(),
    capabilities: [
      {
        id: 'careeros.status',
        kind: 'tool',
        label: 'CareerOS status',
        blockType: 'tool',
        blockSubtype: 'mcp_tool',
        description: 'Health check — documents, chunks, embeddings, achievements.',
      },
      {
        id: 'careeros.semantic_search',
        kind: 'tool',
        label: 'Semantic search',
        blockType: 'tool',
        blockSubtype: 'mcp_tool',
        description: 'Search career knowledge base by meaning.',
      },
      {
        id: 'careeros.tailored_resume',
        kind: 'tool',
        label: 'Generate tailored resume',
        blockType: 'tool',
        blockSubtype: 'mcp_tool',
        description: 'Build ATS-optimized HTML resume from a job description.',
      },
      {
        id: 'careeros.validate_ats',
        kind: 'tool',
        label: 'Validate ATS score',
        blockType: 'tool',
        blockSubtype: 'mcp_tool',
        description: 'Score resume keywords against a role target (90+ goal).',
      },
      {
        id: 'careeros.canonical_cv',
        kind: 'tool',
        label: 'Generate canonical CV',
        blockType: 'tool',
        blockSubtype: 'mcp_tool',
        description: 'Regenerate master CV from mined achievements.',
      },
    ],
    setupSteps: [
      'Start CareerOS locally: `cd career-os && npm run docker:up`',
      'Secrets vault: MCP_CAREEROS_URL = http://127.0.0.1:3301/mcp (or your HTTPS tunnel)',
      'Secrets vault: MCP_CAREEROS_TOKEN = same value as CAREEROS_MCP_TOKEN in career-os/.env',
      'Connect → MCP servers — probe server id `careeros` to cache tools.',
      'Production AutoBlocks blocks localhost — use cloudflared tunnel or deploy CareerOS with HTTPS.',
    ],
  };
}
